import { AREA_PRECISIONS, POST_GEOHASH_PRECISION, WORLD_AREA_KEY } from './constants';
import type { LatLng } from './types';

const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
const GEOHASH_RE = /^[0-9b-hjkmnp-z]+$/;
const EARTH_RADIUS_KM = 6371.0088;
const KM_PER_DEG = (Math.PI * EARTH_RADIUS_KM) / 180;

export interface Bounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export function encodeGeohash(lat: number, lng: number, precision: number): string {
  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;
  let hash = '';
  let bits = 0;
  let ch = 0;
  let evenBit = true;
  while (hash.length < precision) {
    if (evenBit) {
      const mid = (lngMin + lngMax) / 2;
      if (lng >= mid) {
        ch = (ch << 1) | 1;
        lngMin = mid;
      } else {
        ch <<= 1;
        lngMax = mid;
      }
    } else {
      const mid = (latMin + latMax) / 2;
      if (lat >= mid) {
        ch = (ch << 1) | 1;
        latMin = mid;
      } else {
        ch <<= 1;
        latMax = mid;
      }
    }
    evenBit = !evenBit;
    if (++bits === 5) {
      hash += BASE32[ch];
      bits = 0;
      ch = 0;
    }
  }
  return hash;
}

export function isValidGeohash(hash: unknown, precision?: number): hash is string {
  return (
    typeof hash === 'string' &&
    hash.length > 0 &&
    (precision === undefined || hash.length === precision) &&
    GEOHASH_RE.test(hash)
  );
}

export function decodeGeohashBounds(hash: string): Bounds {
  let minLat = -90;
  let maxLat = 90;
  let minLng = -180;
  let maxLng = 180;
  let evenBit = true;
  for (const c of hash) {
    const idx = BASE32.indexOf(c);
    if (idx < 0) throw new Error(`Invalid geohash: ${hash}`);
    for (let n = 4; n >= 0; n--) {
      const bit = (idx >> n) & 1;
      if (evenBit) {
        const mid = (minLng + maxLng) / 2;
        if (bit) minLng = mid;
        else maxLng = mid;
      } else {
        const mid = (minLat + maxLat) / 2;
        if (bit) minLat = mid;
        else maxLat = mid;
      }
      evenBit = !evenBit;
    }
  }
  return { minLat, maxLat, minLng, maxLng };
}

export function geohashCenter(hash: string): LatLng {
  const b = decodeGeohashBounds(hash);
  return { lat: (b.minLat + b.maxLat) / 2, lng: (b.minLng + b.maxLng) / 2 };
}

/** Size of a geohash cell in degrees at a precision. */
export function cellSizeDeg(precision: number): { latDeg: number; lngDeg: number } {
  const bits = precision * 5;
  const lngBits = Math.ceil(bits / 2);
  const latBits = Math.floor(bits / 2);
  return { latDeg: 180 / 2 ** latBits, lngDeg: 360 / 2 ** lngBits };
}

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance in km (haversine). */
export function distanceKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Upper bound of the distance from `p` to the nearest point of a cell (distance to the point of the
 * cell closest in lat/lng). Exact enough to prune cells; callers add a tolerance.
 */
function distanceToCellKm(p: LatLng, b: Bounds): number {
  const lat = Math.min(Math.max(p.lat, b.minLat), b.maxLat);
  let lng = p.lng;
  if (lng < b.minLng || lng > b.maxLng) {
    // Pick the closer edge, accounting for the antimeridian.
    const toMin = Math.abs(((p.lng - b.minLng + 540) % 360) - 180);
    const toMax = Math.abs(((p.lng - b.maxLng + 540) % 360) - 180);
    lng = toMin < toMax ? b.minLng : b.maxLng;
  }
  return distanceKm(p, { lat, lng });
}

/** Geohash of a location at the precision stored on posts. */
export function postGeohash(p: LatLng): string {
  return encodeGeohash(p.lat, p.lng, POST_GEOHASH_PRECISION);
}

export function areaKey(precision: number, cell: string): string {
  return `p${precision}_${cell}`;
}

/** The area boards a post belongs to: one cell per precision, plus the world board. */
export function areaKeysForPost(g5: string | null): string[] {
  if (!g5) return [WORLD_AREA_KEY];
  return [...AREA_PRECISIONS.map((p) => areaKey(p, g5.slice(0, p))), WORLD_AREA_KEY];
}

/** Whether a post with geohash `g5` belongs to the area board `key`. */
export function postInArea(g5: string | null, key: string): boolean {
  if (key === WORLD_AREA_KEY) return true;
  if (!g5) return false;
  const m = /^p(\d)_([0-9b-hjkmnp-z]+)$/.exec(key);
  return m !== null && g5.startsWith(m[2]!);
}

/**
 * Geohash cells at `precision` that the circle around `center` touches. The result is a superset:
 * clients still filter posts by true distance.
 */
export function coverCircle(center: LatLng, radiusKm: number, precision: number): string[] {
  const dLat = radiusKm / KM_PER_DEG;
  const minLat = Math.max(-90, center.lat - dLat);
  const maxLat = Math.min(90, center.lat + dLat);
  // Longitude span is widest at the box edge closest to a pole.
  const poleLat = Math.min(89.999, Math.max(Math.abs(minLat), Math.abs(maxLat)));
  const cos = Math.cos(toRad(poleLat));
  const dLng = radiusKm / (KM_PER_DEG * cos);

  const { latDeg, lngDeg } = cellSizeDeg(precision);
  const nRows = Math.round(180 / latDeg);
  const nCols = Math.round(360 / lngDeg);
  const rowStart = Math.max(0, Math.floor((minLat + 90) / latDeg));
  const rowEnd = Math.min(nRows - 1, Math.floor((maxLat + 90) / latDeg));

  const cols: number[] = [];
  if (dLng >= 180) {
    for (let c = 0; c < nCols; c++) cols.push(c);
  } else {
    const colStart = Math.floor((center.lng - dLng + 180) / lngDeg);
    const colEnd = Math.floor((center.lng + dLng + 180) / lngDeg);
    for (let c = colStart; c <= colEnd; c++) cols.push(((c % nCols) + nCols) % nCols);
  }

  const tolerance = radiusKm * 0.1 + 1;
  const cells = new Set<string>();
  for (let r = rowStart; r <= rowEnd; r++) {
    const lat = -90 + (r + 0.5) * latDeg;
    for (const c of cols) {
      const lng = -180 + (c + 0.5) * lngDeg;
      const cell = encodeGeohash(lat, lng, precision);
      if (distanceToCellKm(center, decodeGeohashBounds(cell)) <= radiusKm + tolerance)
        cells.add(cell);
    }
  }
  return [...cells];
}

/** Area board keys a viewer must read for a circle (`radiusKm: null` = the world board). */
export function areaKeysForView(
  center: LatLng | null,
  radiusKm: number | null,
  precision: number,
): string[] {
  if (!center || radiusKm === null) return [WORLD_AREA_KEY];
  return coverCircle(center, radiusKm, precision).map((cell) => areaKey(precision, cell));
}
